import React, { useState } from "react";
import { Box, Tab, Tabs } from "@mui/material";

import AgentHealthDashboard         from "./AgentHealthDashboard";
import ResponseSpeedDashboard       from "./ResponseSpeedDashboard";
import ServiceInterruptionsDashboard from "./ServiceInterruptionsDashboard";
import DuplicateWatchDashboard      from "./DuplicateWatchDashboard";
import CustomerExperienceDashboard  from "./CustomerExperienceDashboard";
import SpammerDashboard             from "./SpammerDashboard";
import DailyTrendDashboard          from "./DailyTrendDashboard";
import PaymentActivityDashboard     from "./PaymentActivityDashboard";

const BLUE = "#378ADD";

/**
 * ── ONE ENTRY PER TAB, KEYED BY NAME ──
 *
 * Same shape as PowerBIManagerView: `key` is what Tabs carries, not the index,
 * so reordering this array cannot move a selection onto a different page.
 *
 * ── THE ORDER IS AN ESCALATION PATH ──
 * Agent Health answers "is it working right now". Response Speed and Service
 * Interruptions answer "how badly, and to whom". Duplicate Watch is the money
 * question. Customer Experience is the leading indicator that moves before any
 * of the others. Spammer sits next to it because it is the same kind of read —
 * a pattern in what customers send — but the opposite failure: every turn
 * answered fast and correctly, and all of it wasted on a loop. Daily Trend and
 * Payment Activity are the look-back.
 *
 * Someone opening this because a customer complained walks left to right and
 * gets narrower at each step.
 *
 * What Ops DOESN'T carry, and why:
 *
 *   No channel toggle.   The pipeline is the pipeline. A Texts/Emails switch
 *                        would imply these numbers are scoped when they are
 *                        measuring one worker's queue.
 *   No campaign select.  An interruption belongs to a customer waiting for a
 *                        reply, not to the campaign that started the thread.
 *
 *   liveOnly             the page is always NOW and owns its own refresh, so
 *                        the date pills are hidden for it — a range picker on
 *                        a live monitor invites someone to set it to last
 *                        month and then read the result as current.
 */
const TABS = [
  { key: "health",     label: "Agent Health",         liveOnly: true, render: () => <AgentHealthDashboard /> },
  { key: "speed",      label: "Response Speed",       render: () => <ResponseSpeedDashboard /> },
  { key: "outages",    label: "Service Interruptions", render: () => <ServiceInterruptionsDashboard /> },
  { key: "duplicates", label: "Duplicate Watch",      render: () => <DuplicateWatchDashboard /> },
  { key: "experience", label: "Customer Experience",  render: () => <CustomerExperienceDashboard /> },
  { key: "spammers",   label: "Spammer",              render: () => <SpammerDashboard /> },
  { key: "trend",      label: "Daily Trend",          render: () => <DailyTrendDashboard /> },
  { key: "payments",   label: "Payment Activity",     render: () => <PaymentActivityDashboard /> },
];

const VISIBLE = TABS.filter((t) => !t.hidden);

export default function OpsView() {
  const [tabKey, setTabKey] = useState(VISIBLE[0].key);

  // Falls back to the first visible tab rather than rendering nothing, so a key
  // hidden or removed later lands somewhere real instead of leaving MUI with a
  // Tabs value matching no Tab.
  const active = TABS.find((t) => t.key === tabKey && !t.hidden) || VISIBLE[0];

  return (
    <Box sx={{ width: "100%", height: "calc(100vh - 120px)", display: "flex", flexDirection: "column" }}>
      <Box sx={{
        position: "sticky", top: 0, zIndex: 10,
        backgroundColor: "background.paper",
        borderBottom: "0.5px solid", borderColor: "divider",
        display: "flex", alignItems: "center",
        px: 2, gap: 2, minHeight: 48,
      }}>
        <Tabs
          value={active.key}
          onChange={(_, v) => setTabKey(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            flexGrow: 1, minHeight: 48,
            "& .MuiTabs-indicator": {
              height: 2, borderRadius: "2px 2px 0 0", backgroundColor: BLUE,
            },
            "& .MuiTab-root": {
              minHeight: 48, fontSize: 13, fontWeight: 400,
              textTransform: "none", color: "text.secondary", px: 1.5,
              "&.Mui-selected": { color: BLUE, fontWeight: 500 },
            },
          }}
        >
          {VISIBLE.map((t) => (
            <Tab key={t.key} label={t.label} value={t.key} disableRipple />
          ))}
        </Tabs>
      </Box>

      <Box sx={{ flex: 1, overflow: "auto" }}>{active.render()}</Box>
    </Box>
  );
}